import React from 'react';
import {
  Tooltip as AriaTooltip,
  type TooltipTriggerState,
  TooltipTriggerStateContext,
} from 'react-aria-components';

import { tooltipStyles } from '../tooltip/tooltip';
import { OVERLAY_OFFSET, RAC_PLACEMENT } from './placement';

/**
 * An unavailable row's reason pill (menu.md, the unavailable-item mechanism),
 * shared by Menu's items and a picker's options. Not a TooltipTrigger seat: a
 * React Aria menu item or option does not consume the tooltip trigger's
 * Focusable context, so the seat's hover and focus props could never reach
 * it. The row's own render states drive the pill instead, which is also what
 * makes it open on arrow-key focus, where a hover-warmup tooltip never would.
 * Anchored on the row, placed toward the popup's inside edge; same z layer as
 * the popup, and DOM order wins because the pill portals later than the popup.
 */
export function UnavailableReason({
  itemRef,
  isOpen,
  children,
}: {
  itemRef: React.RefObject<HTMLElement | null>;
  isOpen: boolean;
  children: React.ReactNode;
}) {
  /* Seated in context by hand for a 1.19.0 quirk: a standalone Tooltip
   * decides mounting from its own state, but its positioning reads only the
   * context state and crashes on null outside a TooltipTrigger. The no-op
   * open and close are correct: openness derives entirely from the row's
   * hover and focus, so React Aria's hover-out and Escape have nothing to
   * change (Escape reaches the popup and closes it). */
  const state = React.useMemo<TooltipTriggerState>(
    () => ({ isOpen, open: () => undefined, close: () => undefined }),
    [isOpen],
  );
  return (
    <TooltipTriggerStateContext.Provider value={state}>
      <AriaTooltip
        triggerRef={itemRef}
        placement={RAC_PLACEMENT['left']}
        offset={OVERLAY_OFFSET}
        className={tooltipStyles()}
        style={{ zIndex: 'var(--ck-z-overlay)' } as React.CSSProperties}
        data-component="tooltip"
      >
        {children}
      </AriaTooltip>
    </TooltipTriggerStateContext.Provider>
  );
}
